/**
 * Grille des plis d'une partie humaine (stats différé) — même disposition que le drill benchmark ML.
 */

import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { getCardShortName } from '../../utils/cards';
import type { TrickScoreAlternative } from '../../utils/differeStatsReplay';
import DiffereStatsHoverTip from './DiffereStatsHoverTip';
import type { TrickHoverPreviewTarget } from './DiffereStatsTrickHoverPreview';

const GRID_HEADERS = ['Sud', 'Est', 'Nord', 'Ouest', 'Sud', 'Est', 'Nord'] as const;
const MAX_VISIBLE_CELL_BIFS = 2;
const BIF_OVERFLOW_AUTO_CLOSE_MS = 5000;

function studiedSeatColumnIndexes(seat: number): number[] {
  return GRID_HEADERS.map((_, i) => i).filter(i => i % 4 === seat);
}

function formatPrefixMatchPct(rate: number): string {
  const pct = rate * 100;
  if (pct >= 10) return `${Math.round(pct)} %`;
  if (pct >= 1) return `${pct.toFixed(1)} %`;
  if (pct > 0) return '<1 %';
  return '0 %';
}

export interface DiffereStatsDealTrickRow {
  trickIndex: number;
  leader: number;
  winner: number;
  cards: number[];
  players: number[];
}

function formatAltPseudos(alt: TrickScoreAlternative): string {
  const list = alt.bestPseudos?.length ? alt.bestPseudos : [alt.bestPseudo];
  return list.join(', ');
}

function withBifTip(alt: TrickScoreAlternative, child: ReactNode) {
  const isDelayed = alt.kind === 'delayed_bifurcation';
  const isPreVariant = alt.kind === 'pre_bifurcation_variant';
  const preType = alt.preVariantType === 2 ? 2 : isPreVariant ? 1 : null;
  const pseudos = alt.bestPseudos?.length ? alt.bestPseudos : [alt.bestPseudo];
  const title = isPreVariant
    ? (preType === 2
      ? 'Variante avant bifurcation · type 2'
      : 'Variante avant bifurcation · type 1')
    : isDelayed
      ? 'Bifurcation reportée'
      : 'Bifurcation';
  return (
    <DiffereStatsHoverTip
      title={title}
      body={(
        <>
          <div className="differe-stats-hover-tip-row">
            <span className="differe-stats-hover-tip-k">Carte</span>
            <span>{getCardShortName(alt.card as never)}</span>
          </div>
          <div className="differe-stats-hover-tip-row">
            <span className="differe-stats-hover-tip-k">Pli</span>
            <span>
              {alt.trickIndex + 1}
              {(isDelayed || isPreVariant)
                && alt.effectTrickIndex != null
                && alt.effectTrickIndex !== alt.trickIndex
                ? ` → effet pli ${alt.effectTrickIndex + 1}`
                : ''}
            </span>
          </div>
          <div className="differe-stats-hover-tip-row">
            <span className="differe-stats-hover-tip-k">Score</span>
            <span>{alt.bestScore} (+{alt.scoreDelta})</span>
          </div>
          <div className="differe-stats-hover-tip-row">
            <span className="differe-stats-hover-tip-k">Joueurs</span>
            <span>{pseudos.join(', ')}</span>
          </div>
          {isPreVariant && preType === 1 && (
            <div className="differe-stats-hover-tip-muted">
              Au pli d&apos;effet, la 1ère carte différente est jouée par le joueur étudié.
            </div>
          )}
        </>
      )}
    >
      {child}
    </DiffereStatsHoverTip>
  );
}

function isBifurcationAlt(alt: TrickScoreAlternative): boolean {
  return alt.kind !== 'equivalent';
}

function sortBifsByGainDesc(alts: TrickScoreAlternative[]): TrickScoreAlternative[] {
  return [...alts].sort(
    (a, b) => b.scoreDelta - a.scoreDelta
      || a.card - b.card
      || a.bestResultId - b.bestResultId,
  );
}

function BifurcationAltChip({
  alt,
  onAlternativeClick,
}: {
  alt: TrickScoreAlternative;
  onAlternativeClick?: (alt: TrickScoreAlternative, trickIndex: number) => void;
}) {
  const isDelayed = alt.kind === 'delayed_bifurcation';
  const isPreVariant = alt.kind === 'pre_bifurcation_variant';
  const preType = alt.preVariantType === 2 ? 2 : isPreVariant ? 1 : null;
  const altBody = (
    <>
      <span className="differe-stats-deal-tricks-grid-alt-card">
        {getCardShortName(alt.card as never)}
        {preType === 2 && (
          <sup className="differe-stats-deal-tricks-grid-alt-pretype">2</sup>
        )}
      </span>
      <sup className="differe-stats-deal-tricks-grid-alt-gain">
        (+{alt.scoreDelta})
      </sup>
    </>
  );
  const altClass = [
    'differe-stats-deal-tricks-grid-alt',
    isDelayed ? 'differe-stats-deal-tricks-grid-alt--delayed' : '',
    isPreVariant ? 'differe-stats-deal-tricks-grid-alt--pre-variant' : '',
    preType === 2 ? 'differe-stats-deal-tricks-grid-alt--pre-variant-2' : '',
  ].filter(Boolean).join(' ');
  if (onAlternativeClick) {
    return withBifTip(alt, (
      <button
        type="button"
        className={`${altClass} differe-stats-deal-tricks-grid-alt--click`}
        onClick={e => {
          e.stopPropagation();
          onAlternativeClick(alt, alt.trickIndex);
        }}
      >
        {altBody}
      </button>
    ));
  }
  return withBifTip(alt, (
    <span className={altClass}>{altBody}</span>
  ));
}

function CellAlternatives({
  alts,
  onAlternativeClick,
}: {
  alts: TrickScoreAlternative[];
  onAlternativeClick?: (alt: TrickScoreAlternative, trickIndex: number) => void;
}) {
  const equivalents = alts.filter(a => a.kind === 'equivalent');
  const bifs = sortBifsByGainDesc(alts.filter(isBifurcationAlt));
  const visibleBifs = bifs.slice(0, MAX_VISIBLE_CELL_BIFS);
  const hiddenBifs = bifs.slice(MAX_VISIBLE_CELL_BIFS);
  const [moreOpen, setMoreOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoClose = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleAutoClose = () => {
    clearAutoClose();
    timerRef.current = setTimeout(() => setMoreOpen(false), BIF_OVERFLOW_AUTO_CLOSE_MS);
  };

  useEffect(() => {
    if (!moreOpen) {
      clearAutoClose();
      return;
    }
    scheduleAutoClose();
    const onDocDown = (e: globalThis.MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      clearAutoClose();
    };
  }, [moreOpen]);

  const openMore = (e: ReactMouseEvent) => {
    e.stopPropagation();
    setMoreOpen(prev => !prev);
  };

  const onOverflowAltClick = (alt: TrickScoreAlternative, trickIndex: number) => {
    setMoreOpen(false);
    onAlternativeClick?.(alt, trickIndex);
  };

  return (
    <>
      {equivalents.map(alt => (
        <DiffereStatsHoverTip
          key={`eq-${alt.trickIndex}-${alt.card}-${alt.bestResultId}`}
          title="Équivalence (pas de bifurcation)"
          body={(
            <>
              <div>Même classe — {formatAltPseudos(alt)}</div>
              <div className="differe-stats-hover-tip-muted">
                (ou {getCardShortName(alt.card as never)})
              </div>
            </>
          )}
        >
          <span className="differe-stats-deal-tricks-grid-equiv-hint">
            (ou {getCardShortName(alt.card as never)})
          </span>
        </DiffereStatsHoverTip>
      ))}
      {visibleBifs.map(alt => (
        <span key={`bif-${alt.trickIndex}-${alt.card}-${alt.bestResultId}`}>
          <BifurcationAltChip alt={alt} onAlternativeClick={onAlternativeClick} />
        </span>
      ))}
      {hiddenBifs.length > 0 && (
        <span
          ref={wrapRef}
          className="differe-stats-deal-tricks-grid-alt-more-wrap"
        >
          <button
            type="button"
            className={`differe-stats-deal-tricks-grid-alt-more${moreOpen ? ' is-open' : ''}`}
            aria-expanded={moreOpen}
            aria-label={`${hiddenBifs.length} bifurcation${hiddenBifs.length > 1 ? 's' : ''} supplémentaire${hiddenBifs.length > 1 ? 's' : ''}`}
            title="Voir toutes les bifurcations"
            onClick={openMore}
          >
            +{hiddenBifs.length}
          </button>
          {moreOpen && (
            <div
              className="differe-stats-deal-tricks-grid-alt-more-panel"
              role="dialog"
              aria-label="Bifurcations supplémentaires"
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            >
              <div className="differe-stats-deal-tricks-grid-alt-more-panel-head">
                <span>Toutes ({bifs.length})</span>
                <button
                  type="button"
                  className="differe-stats-deal-tricks-grid-alt-more-close"
                  aria-label="Fermer"
                  onClick={e => {
                    e.stopPropagation();
                    setMoreOpen(false);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="differe-stats-deal-tricks-grid-alt-more-panel-list">
                {bifs.map(alt => (
                  <div
                    key={`all-${alt.trickIndex}-${alt.card}-${alt.bestResultId}`}
                    className="differe-stats-deal-tricks-grid-alt-more-panel-row"
                  >
                    <BifurcationAltChip
                      alt={alt}
                      onAlternativeClick={onAlternativeClick ? onOverflowAltClick : undefined}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </span>
      )}
    </>
  );
}

export default function DiffereStatsDealTricksGrid({
  tricks,
  humanSeat = 0,
  hypothesisHumanCards,
  prefixMatchPctByTrick,
  betterScoreAlternativesByTrick,
  peerCardsByTrick,
  focusedTrickNum1,
  onTrickNumberCellClick,
  onAlternativeClick,
  diffSeatKeys,
  equivVariantSeatKeys,
  bifurcationOriginSeatKey,
  highlightLead = true,
  hoverPreviewActive = false,
  onToggleHoverPreview,
  onHoverPreviewTarget,
  forcedPlaySeatKeys,
}: {
  tricks: DiffereStatsDealTrickRow[];
  humanSeat?: number;
  hypothesisHumanCards?: number[];
  prefixMatchPctByTrick?: number[];
  betterScoreAlternativesByTrick?: TrickScoreAlternative[][];
  peerCardsByTrick?: number[][];
  focusedTrickNum1?: number | null;
  onTrickNumberCellClick?: (trickNum1: number) => void;
  onAlternativeClick?: (alt: TrickScoreAlternative, trickIndex: number) => void;
  diffSeatKeys?: ReadonlySet<string>;
  /** Différente de la donne de gauche mais équivalente (classe ou issue). */
  equivVariantSeatKeys?: ReadonlySet<string>;
  /** Carte humaine génératrice de la bifurcation affichée. */
  bifurcationOriginSeatKey?: string;
  highlightLead?: boolean;
  hoverPreviewActive?: boolean;
  onToggleHoverPreview?: () => void;
  onHoverPreviewTarget?: (target: TrickHoverPreviewTarget | null, clientX: number, clientY: number) => void;
  /** `${trickIndex}:${seat}` — seule carte (ou seule classe) légale. */
  forcedPlaySeatKeys?: ReadonlySet<string>;
}) {
  const rows = [...tricks].sort((a, b) => a.trickIndex - b.trickIndex);
  const hypothesisDepth = hypothesisHumanCards?.length ?? 0;
  const studiedCols = new Set(studiedSeatColumnIndexes(humanSeat));

  return (
    <div
      className={`differe-stats-deal-tricks-grid-wrap ml-compare-deal-tricks-grid-wrap${hoverPreviewActive ? ' differe-stats-deal-tricks-grid-wrap--hover-preview' : ''}`}
    >
      <table className="ml-compare-deal-tricks-grid differe-stats-deal-tricks-grid">
        <thead>
          <tr>
            <th className="ml-compare-deal-tricks-grid-pl">Pli</th>
            {GRID_HEADERS.map((h, i) => (
              <th
                key={`${h}-${i}`}
                className={studiedCols.has(i) ? 'ml-compare-deal-tricks-grid-th-studied' : undefined}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(tr => {
            const trickNum1 = tr.trickIndex + 1;
            const leader = tr.leader;
            const startCol = ((leader % 4) + 4) % 4;
            const tdCols: React.ReactNode[] = [];
            for (let col = 0; col < 7; col++) {
              const posInTrick = col - startCol;
              if (posInTrick >= 0 && posInTrick < 4 && posInTrick < tr.cards.length) {
                const card = tr.cards[posInTrick]!;
                const pl = tr.players[posInTrick] ?? ((leader + posInTrick) % 4);
                const isHuman = pl === humanSeat;
                const isLead = highlightLead && isHuman && pl === leader;
                const isHypothesisHumanCard = hypothesisDepth > 0
                  && isHuman
                  && tr.trickIndex < hypothesisDepth
                  && hypothesisHumanCards![tr.trickIndex] === card;
                let cls = 'ml-compare-deal-tricks-grid-cell';
                if (isHypothesisHumanCard) {
                  cls += ' differe-stats-deal-tricks-grid-cell--hypothesis';
                }
                if (isLead) {
                  cls += ' differe-stats-deal-tricks-grid-cell--lead';
                }
                const seatKey = `${tr.trickIndex}:${pl}`;
                const isBifOrigin = bifurcationOriginSeatKey === seatKey;
                const isEquivVariant = !isBifOrigin
                  && equivVariantSeatKeys?.has(seatKey) === true;
                if (isBifOrigin) {
                  cls += ' differe-stats-deal-tricks-grid-cell--bif-origin';
                } else if (isEquivVariant) {
                  cls += ' differe-stats-deal-tricks-grid-cell--equiv-variant';
                } else if (diffSeatKeys?.has(seatKey)) {
                  cls += ' differe-stats-deal-tricks-grid-cell--vs-prev';
                }
                const isForced = forcedPlaySeatKeys?.has(seatKey) === true;
                if (isForced) {
                  cls += ' differe-stats-deal-tricks-grid-cell--forced';
                }
                if (onToggleHoverPreview) {
                  cls += ' differe-stats-deal-tricks-grid-cell--previewable';
                }
                const alts = isHuman ? betterScoreAlternativesByTrick?.[tr.trickIndex] : undefined;
                const previewTarget: TrickHoverPreviewTarget = {
                  trickIndex: tr.trickIndex,
                  posInTrick,
                  seat: pl,
                  card,
                };
                const cardLabel = getCardShortName(card as never);
                const playedInner = isBifOrigin ? (
                  <DiffereStatsHoverTip
                    title="Bifurcation"
                    body="Carte génératrice de la bifurcation affichée."
                  >
                    <span>{cardLabel}</span>
                  </DiffereStatsHoverTip>
                ) : isEquivVariant ? (
                  <DiffereStatsHoverTip
                    title="Variante équivalente"
                    body="Différente de la donne de gauche, mais équivalente selon le mode (classe Type A/B ou même issue)."
                  >
                    <span>{cardLabel}</span>
                  </DiffereStatsHoverTip>
                ) : isForced ? (
                  <DiffereStatsHoverTip
                    title="Coup forcé"
                    body="Seule carte jouable (ou seule classe d'équivalence légale)."
                  >
                    <span>{cardLabel}</span>
                  </DiffereStatsHoverTip>
                ) : (
                  cardLabel
                );
                tdCols.push(
                  <td
                    key={col}
                    className={cls}
                    onClick={onToggleHoverPreview
                      ? e => {
                          e.stopPropagation();
                          const activating = !hoverPreviewActive;
                          onToggleHoverPreview();
                          if (activating && onHoverPreviewTarget) {
                            onHoverPreviewTarget(previewTarget, e.clientX, e.clientY);
                          } else if (!activating && onHoverPreviewTarget) {
                            onHoverPreviewTarget(null, 0, 0);
                          }
                        }
                      : undefined}
                    onMouseEnter={hoverPreviewActive && onHoverPreviewTarget
                      ? e => onHoverPreviewTarget(previewTarget, e.clientX, e.clientY)
                      : undefined}
                    onMouseMove={hoverPreviewActive && onHoverPreviewTarget
                      ? e => onHoverPreviewTarget(previewTarget, e.clientX, e.clientY)
                      : undefined}
                    onMouseLeave={hoverPreviewActive && onHoverPreviewTarget
                      ? () => onHoverPreviewTarget(null, 0, 0)
                      : undefined}
                  >
                    <div className="differe-stats-deal-tricks-grid-cell-inner">
                      <span
                        className={[
                          'differe-stats-deal-tricks-grid-played',
                          isLead ? 'differe-stats-deal-tricks-grid-played--lead' : '',
                          isForced ? 'differe-stats-deal-tricks-grid-played--forced' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {playedInner}
                      </span>
                      {isHuman && peerCardsByTrick?.[tr.trickIndex]?.filter(c => c !== card).map(peerCard => (
                        <DiffereStatsHoverTip
                          key={`peer-${tr.trickIndex}-${peerCard}`}
                          title="Variante équivalente"
                          body={`Jouée par un autre joueur de la même bifurcation : ${getCardShortName(peerCard as never)}`}
                        >
                          <span className="differe-stats-deal-tricks-grid-peer-card">
                            {getCardShortName(peerCard as never)}
                          </span>
                        </DiffereStatsHoverTip>
                      ))}
                      {alts && alts.length > 0 && (
                        <CellAlternatives
                          alts={alts}
                          onAlternativeClick={onAlternativeClick}
                        />
                      )}
                    </div>
                  </td>,
                );
              } else {
                tdCols.push(
                  <td key={col} className="ml-compare-deal-tricks-grid-cell ml-compare-deal-tricks-grid-cell--empty" />,
                );
              }
            }
            return (
              <tr key={tr.trickIndex}>
                <td
                  className={[
                    'ml-compare-deal-tricks-grid-pl',
                    'differe-stats-deal-tricks-grid-pl',
                    focusedTrickNum1 === trickNum1 ? 'ml-compare-deal-tricks-grid-pl-focus' : '',
                    onTrickNumberCellClick ? 'ml-compare-deal-tricks-grid-pl-click' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={
                    onTrickNumberCellClick
                      ? e => {
                          e.stopPropagation();
                          onTrickNumberCellClick(trickNum1);
                        }
                      : undefined
                  }
                >
                  <DiffereStatsHoverTip
                    title="Mains avant ce pli"
                    body={`Clic pour afficher les mains au début du pli ${trickNum1}.`}
                  >
                    <span className="differe-stats-deal-tricks-grid-pl-num">{trickNum1}</span>
                  </DiffereStatsHoverTip>
                  {prefixMatchPctByTrick?.[tr.trickIndex] != null && (
                    <DiffereStatsHoverTip
                      title="% top-X"
                      body={`Part du top-X ayant joué les mêmes cartes humaines (cumul plis 1→${trickNum1}).`}
                    >
                      <span className="differe-stats-deal-tricks-grid-pct">
                        {formatPrefixMatchPct(prefixMatchPctByTrick[tr.trickIndex]!)}
                      </span>
                    </DiffereStatsHoverTip>
                  )}
                </td>
                {tdCols}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
